import { call, fork, put, select, takeLatest } from "redux-saga/effects";

// Store
import { RootState } from "store";

// Saga helper
import {
	searchRequestListByIds,
	searchRequestDrugsListByIds,
	searchRequestImpuritiesListByIds,
	searchRequestChemicalProbesListByIds,
} from "./saga-helper";

// Actions
import { hideLoader } from "reducers/loader";
import * as actionsCreator from "../reducer";

// Lib
import { convertToUniqueIdArray, isEngLang } from 'lib/helpers';

// Type

function* _select<T>(fn: (state: RootState) => T) {
	const res: T = yield select(fn);
	return res;
}

export const search = (store: RootState) => store.search;

// Start Sagas
export function* searchTextListByIdsSagaStart(api: any, action: any) {
	yield fork(searchTextListByIdsDrugsWatcher, api);
	yield fork(searchTextListByIdsCompoundWatcher, api);
	yield fork(searchTextListByIdsImpuritiesWatcher, api);
	yield fork(searchTextListByIdsChemicalProbesWatcher, api);
}

/**
 *  search text compounds Watcher Sagas starter
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchTextListByIdsCompoundWatcher(api: any) {
	yield takeLatest(
		actionsCreator.searchTextByIds.type,
		searchTextListByIdsCompoundWorker,
		api
	);
}

/**
 *  search text drugs Watcher Sagas starter
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchTextListByIdsDrugsWatcher(api: any) {
	yield takeLatest(
		actionsCreator.searchTextByIds.type,
		searchTextListByIdsDrugsWorker,
		api
	);
}

/**
 *  search text impurities Watcher Sagas starter
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchTextListByIdsImpuritiesWatcher(api: any) {
	yield takeLatest(
		actionsCreator.searchTextByIds.type,
		searchTextListByIdsImpuritiesWorker,
		api
	);
}

/**
 *  search text impurities Watcher Sagas starter
 *
 * @param {function} api - api object
 * @returns
 */
export function* searchTextListByIdsChemicalProbesWatcher(api: any) {
	yield takeLatest(
		actionsCreator.searchTextByIds.type,
		searchTextListByIdsChemicalProbesWorker,
		api
	);
}

/**
 *  Post search text for compounds Watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchTextListByIdsCompoundWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;

		const state = yield* _select(search);
		const isUpdateData = action.payload.type === 'update-data';
		const type = state?.searchListByIds?.type;
		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			yield put(actionsCreator.searchTextByIdsCompoundsLoadingTab());
			const convertedArray = convertToUniqueIdArray(value);

			const res = yield call(searchRequestListByIds, api, convertedArray);

			if (!res.ok) {
				yield put(actionsCreator.searchTextByIdsError({
					type: "error",
					message: `Error in request ${type} search`
				}));
				yield put(hideLoader());
			} else {
				const formData = {
					data: {
						size: 5,
						number: 0,
						content: res.data?.content || [],
						totalElements: res?.data?.content?.length || 0,
						totalPages: Math.ceil(res.data?.content?.length / 5) || 1,
					},
					resultsOverview: {
						inputIds: convertedArray?.length,
						notFoundIds: res.data?.notFoundIds,
					}
				};

				yield put(actionsCreator.searchTextByIdsCompoundsSuccess(formData));

				if (isUpdateData) {
					yield put(actionsCreator.updateSearchByIdsData());
				}
				yield put(hideLoader());
			}
		} else {
			yield put(actionsCreator.searchTextByIdsError({
				type: "info",
				message: "Please use Latin letters only"
			}));
			yield put(hideLoader());
		}
	} catch (e) {
		console.log("ERROR searchTextCompoundWorker", e);
	}
}

/**
 *  Post Search Text Watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchTextListByIdsDrugsWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;

		const state = yield* _select(search);
		const type = state?.searchListByIds?.type;
		const isUpdateData = action.payload.type === 'update-data';
		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			yield put(actionsCreator.searchTextByIdsDrugLoadingTab());
			const convertedArray = convertToUniqueIdArray(value);

			const res = yield call(searchRequestDrugsListByIds, api, convertedArray);

			if (!res.ok) {
				yield put(actionsCreator.searchTextByIdsError({
					type: "error",
					message: `Error in request ${type} search`
				}));
				yield put(hideLoader());
			} else {
				const formData = {
					data: {
						size: 5,
						number: 0,
						content: res.data?.content || [],
						totalElements: res?.data?.content?.length || 0,
						totalPages: Math.ceil(res.data?.content?.length / 5) || 1,
					},
					resultsOverview: {
						inputIds: convertedArray?.length,
						notFoundIds: res.data?.notFoundIds,
					}
				};

				yield put(actionsCreator.searchTextByIdsDrugSuccess(formData));
				if (isUpdateData) {
					yield put(actionsCreator.updateSearchByIdsData());
				}
				yield put(hideLoader());
			}
		} else {
			yield put(actionsCreator.searchTextByIdsError({
				type: "info",
				message: "Please use Latin letters only"
			}));
			yield put(hideLoader());
		}
	} catch (e) {
		console.log("ERROR searchTextDrugsWorker", e);
	}
}

/**
 *  Post Search Text Watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchTextListByIdsImpuritiesWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;

		const state = yield* _select(search);
		const type = state?.searchListByIds?.type;
		const isUpdateData = action.payload.type === 'update-data';
		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			yield put(actionsCreator.searchTextByIdsImpuritiesLoadingTab());
			const convertedArray = convertToUniqueIdArray(value);

			const res = yield call(searchRequestImpuritiesListByIds, api, convertedArray);

			if (!res.ok) {
				yield put(actionsCreator.searchTextByIdsError({
					type: "error",
					message: `Error in request ${type} search`
				}));
				yield put(hideLoader());
			} else {
				const formData = {
					data: {
						size: 5,
						number: 0,
						content: res.data?.content || [],
						totalElements: res?.data?.content?.length || 0,
						totalPages: Math.ceil(res.data?.content?.length / 5) || 1,
					},
					resultsOverview: {
						inputIds: convertedArray?.length,
						notFoundIds: res.data?.notFoundIds,
					}
				};

				yield put(actionsCreator.searchTextByIdsImpuritiesSuccess(formData));
				if (isUpdateData) {
					yield put(actionsCreator.updateSearchByIdsData());
				}
				yield put(hideLoader());
			}
		} else {
			yield put(actionsCreator.searchTextByIdsError({
				type: "info",
				message: "Please use Latin letters only"
			}));
			yield put(hideLoader());
		}
	} catch (e) {
		console.log("ERROR searchTextImpuritiesWorker", e);
	}
}

/**
 *  Post Search Text Watcher Sagas starter
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* searchTextListByIdsChemicalProbesWorker(api: any, action: any): Generator<any, void, any> {
	try {
		const value = action.payload.value;

		const state = yield* _select(search);
		const type = state?.searchListByIds?.type;
		const isUpdateData = action.payload.type === 'update-data';
		const isEng = isEngLang(value);

		if (!value) {
			return;
		}

		if (isEng) {
			yield put(actionsCreator.searchTextByIdsChemicalProbesLoadingTab());
			const convertedArray = convertToUniqueIdArray(value);

			const res = yield call(searchRequestChemicalProbesListByIds, api, convertedArray);

			if (!res.ok) {
				yield put(actionsCreator.searchTextByIdsError({
					type: "error",
					message: `Error in request ${type} search`
				}));
				yield put(hideLoader());
			} else {
				const formData = {
					data: {
						size: 5,
						number: 0,
						content: res.data?.content || [],
						totalElements: res?.data?.content?.length || 0,
						totalPages: Math.ceil(res.data?.content?.length / 5) || 1,
					},
					resultsOverview: {
						inputIds: convertedArray?.length,
						notFoundIds: res.data?.notFoundIds,
					}
				};

				yield put(actionsCreator.searchTextByIdsChemicalProbesSuccess(formData));
				if (isUpdateData) {
					yield put(actionsCreator.updateSearchByIdsData());
				}
				yield put(hideLoader());
			}
		} else {
			yield put(actionsCreator.searchTextByIdsError({
				type: "info",
				message: "Please use Latin letters only"
			}));
			yield put(hideLoader());
		}
	} catch (e) {
		console.log("ERROR searchTextChemicalProbesWorker", e);
	}
}
